import { t } from 'i18next';
import { ExternalAccountType, MerchantBusinessCategories } from 'services/merchants/types';
import * as Yup from 'yup';

import getQueryParam from 'utils/url';

import { LABELS } from '../../keys';

export const FORM_FIELDS = {
  ACCOUNT_TYPE: 'accountType',
  BANK_NAME: 'bankName',
  CLABE: 'clabe',
  ACCOUNT_OWNER_NAME: 'accountOwnerName',
  ADDRESS: 'address',
} as const;

export const NESTED_ACCOUNT_FIELDS = {
  ACCOUNT_NUMBER: 'accountNumber',
  STREET_LINE_1: 'streetLine1',
  STREET_LINE_2: 'streetLine2',
  CITY: 'city',
  STATE: 'state',
  POSTAL_CODE: 'postalCode',
  ADDRESS_COUNTRY: 'country',
} as const;

export const KEYS = {
  ROUTING_NUMBER_MAX_LENGTH: 9,
  UNASSOCIATED_ERROR: 'unassociatedError',
  // For this payment rail we support mxn only
  // Even if merchant has currency MXN we would need to lowercase it.
  // Hard coded currency brings less confusion in this case
  HARDCODED_CURRENCY: 'mxn',
};

export type SpeiFormField = (typeof FORM_FIELDS)[keyof typeof FORM_FIELDS];
export type SpeiFormNestedField = (typeof NESTED_ACCOUNT_FIELDS)[keyof typeof NESTED_ACCOUNT_FIELDS];

export type SpeiFormValues = {
  [FORM_FIELDS.ACCOUNT_TYPE]: ExternalAccountType | undefined;
  [FORM_FIELDS.CLABE]: {
    [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: string;
  };
  [FORM_FIELDS.BANK_NAME]?: string;
  [FORM_FIELDS.ACCOUNT_OWNER_NAME]: string | undefined;
  [FORM_FIELDS.ADDRESS]?: {
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: string;
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: string | undefined;
    [NESTED_ACCOUNT_FIELDS.CITY]: string;
    [NESTED_ACCOUNT_FIELDS.STATE]: string;
    [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: string;
    [NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY]: string;
  };
};

export type SpeiNestedFormValues = {
  [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.CITY]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.STATE]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: string | undefined;
  [NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY]: string | undefined;
};

export const categoryOptions = [
  {
    key: MerchantBusinessCategories.CLIENT,
    label: t(LABELS.CREATE_MODAL.CATEGORY.CLIENT),
  },
  {
    key: MerchantBusinessCategories.PARENT_COMPANY,
    label: t(LABELS.CREATE_MODAL.CATEGORY.PARENT_COMPANY),
  },
  {
    key: MerchantBusinessCategories.SUBSIDIARY,
    label: t(LABELS.CREATE_MODAL.CATEGORY.SUBSIDIARY),
  },
  {
    key: MerchantBusinessCategories.SUPPLIER,
    label: t(LABELS.CREATE_MODAL.CATEGORY.SUPPLIER),
  },
];

export const getValidationSchema = () =>
  Yup.object().shape({
    [FORM_FIELDS.CLABE]: Yup.object().shape({
      [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    }),
    [FORM_FIELDS.BANK_NAME]: Yup.string().optional(),
    [FORM_FIELDS.ACCOUNT_OWNER_NAME]: Yup.string().required(t(LABELS.ERRORS.REQUIRED_FIELD)),
    [FORM_FIELDS.ADDRESS]: Yup.object().shape({
      [NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY]: Yup.string().optional(),
      [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: Yup.string().optional(),
      [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: Yup.string().optional(),
      [NESTED_ACCOUNT_FIELDS.CITY]: Yup.string().optional(),
      [NESTED_ACCOUNT_FIELDS.STATE]: Yup.string().optional(),
      [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: Yup.string().optional(),
    }),
  });

const initialValues: SpeiFormValues = {
  [FORM_FIELDS.ACCOUNT_TYPE]: ExternalAccountType.CLABE,
  [FORM_FIELDS.BANK_NAME]: '',
  [FORM_FIELDS.ACCOUNT_OWNER_NAME]: undefined,
  [FORM_FIELDS.CLABE]: {
    [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: '',
  },
  [FORM_FIELDS.ADDRESS]: {
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_1]: '',
    [NESTED_ACCOUNT_FIELDS.STREET_LINE_2]: undefined,
    [NESTED_ACCOUNT_FIELDS.CITY]: '',
    [NESTED_ACCOUNT_FIELDS.STATE]: '',
    [NESTED_ACCOUNT_FIELDS.POSTAL_CODE]: '',
    [NESTED_ACCOUNT_FIELDS.ADDRESS_COUNTRY]: '',
  },
};

export const getPrefilledValues = (): SpeiFormValues => {
  const prefillDisabledWithQueryParam = getQueryParam('disablePrefill') === 'true';
  const prefillDisabled = window.s4cConfig.features?.canPrefillBankForm !== true;
  if (prefillDisabled || prefillDisabledWithQueryParam) {
    return initialValues;
  }

  return {
    [FORM_FIELDS.ACCOUNT_TYPE]: ExternalAccountType.CLABE,
    [FORM_FIELDS.ACCOUNT_OWNER_NAME]: 'John Doe',
    [FORM_FIELDS.CLABE]: {
      [NESTED_ACCOUNT_FIELDS.ACCOUNT_NUMBER]: '000000000123456789',
    },
  };
};
